import React from "react";
import useCompanyDepartmentsTable from "./useCompanyDepartmentsTable";
import Resources from "../../types/Resources";
import LoadingSpinner from "../Common/LoadingSpinner";
import Button from "../Common/Button";
import CreateCompanyDepartmentModal from "../Companies/CreateCompanyDepartmentModal";
import UpdateCompanyDepartmentModal from "../Companies/UpdateCompanyDepartmentModal";
import DeleteCompanyDepartmentModal from "../Companies/DeleteCompanyDepartmentModal";

export type CompanyDepartmentsTableProps = {
    resources: Resources;
    companyId: string;
    canManageIsEnergyHarbourCustomer: boolean;
};

function CompanyDepartmentsTable(props: CompanyDepartmentsTableProps) {
    const { resources } = props;
    const pageContent = useCompanyDepartmentsTable(props);

    if (pageContent.isLoading) {
        return (
            <div className="mt-5 pt-5">
                <LoadingSpinner />
            </div>
        );
    }

    return (
        <>
            <div className="d-flex justify-content-center">
                <div className="m-3" style={{ width: "100%", maxWidth: "1200px" }}>
                    <div className="d-flex justify-content-between mb-2">
                        <h1 className="h4">
                            {resources.get("CompanyDepartmentsFor")} {pageContent.company?.name}
                        </h1>
                        <Button onClick={pageContent.handleShowCreateCompanyDepartmentModal}>
                            <i className="fas fa-plus me-1"></i>
                            {resources.get("NewCompanyDepartment")}
                        </Button>
                    </div>
                    <table className="table table-striped table-bordered mb-0 shadow-sm">
                        <thead>
                            <tr>
                                <th className="fw-bold">{resources.get("Name")}</th>
                                <th className="fw-bold text-end">{resources.get("NumberOfUsers")}</th>
                                <th className="fw-bold" style={{ width: "64px" }}></th>
                            </tr>
                        </thead>
                        <tbody>
                            {pageContent.companyDepartments?.map((cd) => (
                                <tr key={cd.id}>
                                    <td>{cd.name}</td>
                                    <td className="text-end">{cd.numberOfUsers}</td>
                                    <td className="d-flex justify-content-center gap-2" >
                                        {cd.canUpdate ? (
                                            <Button
                                                aria-label={resources.get("UpdateDepartment")}
                                                tooltip={resources.get("UpdateDepartment")}
                                                variant="secondary"
                                                onClick={() =>
                                                    pageContent.handleShowUpdateCompanyDepartmentModal(cd.id)
                                                }
                                            >
                                                <i className="fas fa-pen"></i>
                                            </Button>
                                        ) : null}
                                        {cd.canDelete ? (
                                            <Button
                                                aria-label={resources.get("DeleteDepartment")}
                                                tooltip={resources.get("DeleteDepartment")}
                                                variant="danger"
                                                onClick={() => pageContent.handleShowDeleteCompanyDepartmentModal(cd.id)}
                                            >
                                                <i className="fas fa-trash"></i>
                                            </Button>
                                        ) : null}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
            <CreateCompanyDepartmentModal
                show={pageContent.showCreateCompanyDepartmentModal}
                companyId={props.companyId}
                resources={props.resources}
                canManageIsEnergyHarbourCustomer={props.canManageIsEnergyHarbourCustomer}
                onDepartmentCreated={pageContent.handleCompanyDepartmentCreated}
                onHide={pageContent.handleCloseCreateCompanyDepartmentModal}
            />
            <UpdateCompanyDepartmentModal
                departmentId={pageContent.updateCompanyDepartmentId ?? 0}
                show={pageContent.showUpdateCompanyDepartmentModal}
                resources={props.resources}
                canManageIsEnergyHarbourCustomer={props.canManageIsEnergyHarbourCustomer}
                onDepartmentUpdated={pageContent.handleCompanyDepartmentUpdated}
                onHide={pageContent.handleCloseUpdateCompanyDepartmentModal}
            />
            <DeleteCompanyDepartmentModal
                departmentId={pageContent.deleteCompanyDepartmentId ?? 0}
                show={pageContent.showDeleteCompanyDepartmentModal}
                resources={props.resources}
                onDepartmentDeleted={pageContent.handleCompanyDepartmentDeleted}
                onHide={pageContent.handleCloseDeleteCompanyDepartmentModal}
            />
        </>
    );
}

export default CompanyDepartmentsTable;
